<template>
  <section
    id="ai-review"
    class="section ai-review-system"
  >
    <div class="container">
      <div class="section-title">
        <h2>PCB 版图智能审查工具</h2>
        <p>上传 PCB 文件，30 分钟获得专业审查报告 - 准确率 99%+</p>
        <div class="title-cta">
          <button
            class="btn btn-primary btn-large"
            @click="handleTryTool"
          >
            <i class="fas fa-play-circle" /> 立即使用工具
          </button>
          <span class="free-badge">免费试用</span>
        </div>
      </div>

      <!-- 工具核心优势（合并版） -->
      <div class="tool-advantages">
        <h3 class="advantages-title">
          核心优势
        </h3>
        <div class="advantages-grid">
          <div class="advantage-card">
            <div class="adv-icon">
              <i class="fas fa-bolt" />
            </div>
            <h4>审查速度</h4>
            <div class="adv-comparison">
              <div class="adv-old">
                传统：7-14 天
              </div>
              <div class="adv-arrow">
                <i class="fas fa-long-arrow-alt-right" />
              </div>
              <div class="adv-new">
                AI：30 分钟
              </div>
            </div>
            <div class="adv-result">
              28 倍提速
            </div>
          </div>

          <div class="advantage-card">
            <div class="adv-icon">
              <i class="fas fa-bullseye" />
            </div>
            <h4>检出准确率</h4>
            <div class="adv-comparison">
              <div class="adv-old">
                传统：60-70%
              </div>
              <div class="adv-arrow">
                <i class="fas fa-long-arrow-alt-right" />
              </div>
              <div class="adv-new">
                AI：99%+
              </div>
            </div>
            <div class="adv-result">
              300+ 检查项
            </div>
          </div>

          <div class="advantage-card">
            <div class="adv-icon">
              <i class="fas fa-robot" />
            </div>
            <h4>自动化程度</h4>
            <div class="adv-comparison">
              <div class="adv-old">
                传统：手动编码
              </div>
              <div class="adv-arrow">
                <i class="fas fa-long-arrow-alt-right" />
              </div>
              <div class="adv-new">
                AI：全自动
              </div>
            </div>
            <div class="adv-result">
              零手动编码
            </div>
          </div>

          <div class="advantage-card">
            <div class="adv-icon">
              <i class="fas fa-coins" />
            </div>
            <h4>审查成本</h4>
            <div class="adv-comparison">
              <div class="adv-old">
                传统：8000+ 元
              </div>
              <div class="adv-arrow">
                <i class="fas fa-long-arrow-alt-right" />
              </div>
              <div class="adv-new">
                AI：1500 元
              </div>
            </div>
            <div class="adv-result">
              节省 80%
            </div>
          </div>
        </div>
      </div>

      <!-- AI智能审查工作原理 -->
      <div class="how-it-works">
        <h3 class="works-title">
          AI 如何完成智能审查？
        </h3>
        <p class="works-subtitle">
          基于大模型和 Agent 技术的四大智能模块
        </p>
        
        <div class="works-flow">
          <div class="works-step">
            <div class="step-number">
              01
            </div>
            <h4><i class="fas fa-project-diagram" /> AI 智能识别审查对象</h4>
            <p class="step-highlight">
              电路解析模块
            </p>
            <p>AI Agent 自动解析版图与原理图，智能识别检视对象</p>
            <ul class="step-details">
              <li>版图几何信息：信号层、电源层、接地层、丝印层等</li>
              <li>网表连接关系：元件和网络的连接信息</li>
              <li>模块电路识别：功能模块和电路结构</li>
            </ul>
          </div>

          <div class="works-step">
            <div class="step-number">
              02
            </div>
            <h4><i class="fas fa-sitemap" /> AI 智能分析检查规划</h4>
            <p class="step-highlight">
              规则解析模块
            </p>
            <p>LLM 理解审查规则，智能分类并构建知识图谱</p>
            <ul class="step-details">
              <li>LLM 理解并分类：几何规则、高速信号规则、电源规则等</li>
              <li>结构化描述：形成 DRC 审查规则知识图谱</li>
              <li>知识库支撑：历史记录、器件手册、电路基础知识</li>
            </ul>
          </div>

          <div class="works-step">
            <div class="step-number">
              03
            </div>
            <h4><i class="fas fa-code" /> 智能审查代码生成</h4>
            <p class="step-highlight">
              代码生成模块
            </p>
            <p>Agent 自动生成 DRC 审查代码并执行检测</p>
            <ul class="step-details">
              <li>自动生成审查代码：基于规则知识图谱智能生成</li>
              <li>代码自检修复：Agent 检视并修复代码 bug</li>
              <li>调用算子库：距离检测、方向检测等 2D 图形计算</li>
            </ul>
          </div>

          <div class="works-step">
            <div class="step-number">
              04
            </div>
            <h4><i class="fas fa-clipboard-check" /> AI 智能结果判定</h4>
            <p class="step-highlight">
              结果判定模块
            </p>
            <p>Agent 智能判断设计满足度，生成详细报告</p>
            <ul class="step-details">
              <li>智能判定：根据规则判断版图设计的满足程度</li>
              <li>历史学习：结合历史审查记录优化判断</li>
              <li>生成报告：问题分类、统计、优化建议</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- PCB审查整体架构流程 -->
      <div class="review-architecture">
        <h3 class="architecture-title">
          PCB 版图智能审查整体架构
        </h3>
        <div class="architecture-diagram">
          <div class="arch-input">
            <div class="arch-box">
              <i class="fas fa-file-image" />
              <span>版图 / 原理图</span>
            </div>
          </div>

          <div class="arch-core-modules">
            <div class="arch-module">
              <div class="arch-module-header">
                电路解析模块
              </div>
              <div class="arch-module-content">
                <small>解析原理图、版图信息</small>
                <small>Agent识别并划分电路功能模块</small>
              </div>
            </div>

            <div class="arch-arrow-down">
              <div class="arrow-label">
                网表、电路物理位置信息
              </div>
              <i class="fas fa-arrow-down" />
            </div>

            <div class="arch-code-judge">
              <div class="arch-module yellow">
                <div class="arch-module-header">
                  代码生成模块
                </div>
                <div class="arch-module-content">
                  <small>基于DRC审查规则知识图谱</small>
                  <small>Agent对输入的版图及电路</small>
                  <small>模块生成对应规则的审查代码</small>
                </div>
              </div>

              <div class="arch-module yellow">
                <div class="arch-module-header">
                  结果判定模块
                </div>
                <div class="arch-module-content">
                  <small>基于DRC审查规则知识图谱</small>
                  <small>Agent根据判定类规则对</small>
                  <small>版图和电路模块进行满足度判断</small>
                </div>
              </div>
            </div>

            <div class="arch-rules-module">
              <div class="arch-module-header">
                规则解析模块
              </div>
              <div class="arch-module-content">
                通过历史审查记录、审查清单、电路基础知识、器件手册等，
                Agent构建DRC审查规则知识图谱
              </div>
              <div class="arch-knowledge-base">
                <span>历史审查记录</span>
                <span>审查清单</span>
                <span>器件手册</span>
                <span>电路基础知识</span>
              </div>
            </div>
          </div>

          <div class="arch-output">
            <div class="arch-box">
              <i class="fas fa-file-alt" />
              <span>审查报告</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 审查能力概览 -->
      <div class="review-capabilities">
        <h3 class="capabilities-title">
          全面覆盖 6 大审查维度
        </h3>
        <div class="capabilities-grid">
          <div class="cap-item">
            <i class="fas fa-project-diagram" />
            <span>原理图审查</span>
          </div>
          <div class="cap-item">
            <i class="fas fa-th" />
            <span>PCB 布局审查</span>
          </div>
          <div class="cap-item">
            <i class="fas fa-broadcast-tower" />
            <span>EMC 合规性</span>
          </div>
          <div class="cap-item">
            <i class="fas fa-industry" />
            <span>可制造性 (DFM)</span>
          </div>
          <div class="cap-item">
            <i class="fas fa-shield-alt" />
            <span>可靠性分析</span>
          </div>
          <div class="cap-item">
            <i class="fas fa-certificate" />
            <span>安全合规</span>
          </div>
        </div>
      </div>

      <!-- 实际案例展示 -->
      <div class="case-example">
        <h3 class="case-title">
          PCB 审查案例示例
        </h3>
        <div class="case-content">
          <div class="case-rule">
            <h4><i class="fas fa-clipboard-list" /> 检视规则</h4>
            <p>CLK/DQS 与 AC/DQ 距离大于 2mm</p>
            <p>单端信号 AC/DQ 0.4mm-10mm 内有接地点</p>
            <p>AC/DQ 与地点的连线垂直或平行于 CLK/DQS 的 PN 点连线</p>
          </div>
          
          <div class="case-process">
            <h4><i class="fas fa-cogs" /> 审查流程</h4>
            <div class="case-steps">
              <div class="case-step">
                <div class="step-badge">
                  1
                </div>
                <div class="step-desc">
                  <strong>电路解析模块</strong>
                  <p>从PCB版图中提取出相关的网络和元件：CLK/DQS（时钟或数据选通信号）、AC/DQ（数据信号）、GND（地网络）</p>
                </div>
              </div>
              <div class="case-step">
                <div class="step-badge">
                  2
                </div>
                <div class="step-desc">
                  <strong>规则解析模块</strong>
                  <p><strong>距离检测：</strong>检查 CLK/DQS 与 AC/DQ 的距离</p>
                  <p><strong>位置检测：</strong>检查 AC/DQ 附近是否有接地点</p>
                  <p><strong>方向检测：</strong>检查 AC/DQ 与地点的连线方向是否与 CLK/DQS 的 PN 点连线方向垂直或平行</p>
                </div>
              </div>
              <div class="case-step">
                <div class="step-badge">
                  3
                </div>
                <div class="step-desc">
                  <strong>代码生成与检查</strong>
                  <p>代码生成与检查，tools调用：距离检测、方向向量检测等</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="review-cta">
        <h3>立即使用 PCB 智能审查工具</h3>
        <p>上传您的 PCB 设计文件（Gerber/ODB++），30 分钟获得专业审查报告</p>
        <div class="cta-buttons">
          <button
            class="btn btn-primary btn-large"
            @click="handleTryTool"
          >
            <i class="fas fa-tools" /> 进入审查工具
          </button>
          <button class="btn btn-outline btn-large">
            <i class="fas fa-play-circle" /> 观看演示
          </button>
        </div>
        <div class="cta-note">
          <i class="fas fa-info-circle" /> 免费试用 · 30分钟出结果
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const handleTryTool = () => {
  // 跳转到 PCB 审查工具页面
  // window.location.href = "/pcb-review-tool"; // 工具页面路径
  alert("即将进入 PCB 智能审查工具");
};
</script>

<style scoped>
.ai-review-system {
  background: linear-gradient(to bottom, #f9fafb, white);
  padding: 100px 0;
}

.section-title {
  text-align: center;
  margin-bottom: 80px;
}

.section-title h2 {
  font-size: 2.5rem;
  color: #1f2937;
  margin-bottom: 20px;
  font-weight: 700;
}

.section-title p {
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 800px;
  margin: 0 auto 30px;
}

.title-cta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
}

.free-badge {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

/* 工具核心优势（合并版） */
.tool-advantages {
  margin-bottom: 80px;
}

.advantages-title {
  text-align: center;
  font-size: 1.8rem;
  color: #1f2937;
  margin-bottom: 40px;
  font-weight: 700;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 25px;
}

.advantage-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 30px 25px;
  text-align: center;
  transition: all 0.2s ease;
}

.advantage-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.advantage-card .adv-icon {
  width: 50px;
  height: 50px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #6b7280;
  font-size: 24px;
}

.advantage-card h4 {
  font-size: 1.1rem;
  color: #1f2937;
  margin-bottom: 15px;
  font-weight: 600;
}

.adv-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
  min-height: 50px;
  flex-wrap: wrap;
}

.adv-comparison .adv-old {
  font-size: 0.85rem;
  color: #9ca3af;
  text-decoration: line-through;
}

.adv-comparison .adv-arrow {
  color: #d1d5db;
}

.adv-comparison .adv-arrow i {
  font-size: 20px;
}

.adv-comparison .adv-new {
  font-size: 1.1rem;
  color: #1f2937;
  font-weight: 700;
}

.adv-result {
  font-size: 1.2rem;
  font-weight: 700;
  color: #4b5563;
}

/* AI工作原理 */
.how-it-works {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 50px 40px;
  margin-bottom: 80px;
}

.works-title {
  text-align: center;
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 10px;
  font-weight: 700;
}

.works-subtitle {
  text-align: center;
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 40px;
}

.works-flow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
}

.works-step {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 25px;
}

.works-step .step-number {
  display: inline-block;
  background: #6b7280;
  color: white;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  text-align: center;
  line-height: 35px;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 15px;
}

.works-step h4 {
  font-size: 1.2rem;
  color: #1f2937;
  margin-bottom: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.works-step h4 i {
  color: #6b7280;
}

.step-highlight {
  font-size: 0.95rem;
  color: #4b5563;
  font-weight: 600;
  margin-bottom: 8px;
}

.works-step > p {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 15px;
  line-height: 1.6;
}

.step-details {
  list-style: none;
  padding-left: 0;
}

.step-details li {
  padding: 6px 0 6px 15px;
  font-size: 0.85rem;
  color: #6b7280;
  position: relative;
  line-height: 1.5;
}

.step-details li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #9ca3af;
}

/* 核心优势对比 */
.key-advantages {
  margin-bottom: 80px;
}

.advantages-title {
  text-align: center;
  font-size: 1.8rem;
  color: #1f2937;
  margin-bottom: 40px;
  font-weight: 700;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 25px;
}

.advantage-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  transition: all 0.2s ease;
}

.advantage-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.adv-icon {
  width: 50px;
  height: 50px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #6b7280;
  font-size: 24px;
}

.adv-compare {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 15px;
  min-height: 50px;
  flex-wrap: wrap;
}

.adv-old {
  font-size: 0.85rem;
  color: #9ca3af;
  text-decoration: line-through;
}

.adv-arrow {
  color: #d1d5db;
  font-weight: 700;
  font-size: 1.2rem;
}

.adv-new {
  font-size: 1.2rem;
  color: #1f2937;
  font-weight: 700;
}

.adv-label {
  font-size: 0.95rem;
  color: #6b7280;
  font-weight: 600;
}

/* 暂时隐藏架构图 - 改为更简洁的展示 */
.review-architecture {
  display: none;
}

/* 审查能力概览 */
.review-capabilities {
  margin-bottom: 80px;
}

.capabilities-title {
  text-align: center;
  font-size: 1.8rem;
  color: #1f2937;
  margin-bottom: 30px;
  font-weight: 700;
}

.capabilities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;
}

.cap-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  transition: all 0.2s ease;
}

.cap-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.cap-item i {
  font-size: 32px;
  color: #6b7280;
}

.cap-item span {
  font-size: 0.95rem;
  color: #1f2937;
  font-weight: 600;
}

/* 案例展示 */
.case-example {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 40px;
  margin-bottom: 60px;
}

.case-title {
  text-align: center;
  font-size: 1.8rem;
  color: #1f2937;
  margin-bottom: 30px;
  font-weight: 700;
}

.case-content {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 40px;
}

.case-rule {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 25px;
}

.case-rule h4 {
  font-size: 1.1rem;
  color: #1f2937;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.case-rule p {
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 12px;
  padding-left: 10px;
  border-left: 3px solid #6b7280;
  line-height: 1.6;
}

.case-process h4 {
  font-size: 1.1rem;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.case-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.case-step {
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.step-badge {
  width: 32px;
  height: 32px;
  background: #6b7280;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.step-desc {
  flex: 1;
}

.step-desc strong {
  display: block;
  color: #1f2937;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.step-desc p {
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 6px;
}

/* CTA */
.review-cta {
  text-align: center;
  padding: 60px;
  background: linear-gradient(135deg, #6b7280, #4b5563);
  border-radius: 8px;
  color: white;
}

.review-cta h3 {
  font-size: 2rem;
  margin-bottom: 15px;
  font-weight: 700;
}

.review-cta p {
  font-size: 1.1rem;
  margin-bottom: 30px;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.btn-large {
  padding: 18px 40px;
  font-size: 1.2rem;
}

.btn-outline {
  background: transparent;
  border: 2px solid white;
  color: white;
  transition: all 0.2s ease;
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
}

.cta-note {
  font-size: 0.9rem;
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.cta-note i {
  font-size: 14px;
}

/* 响应式 */
@media (max-width: 1024px) {
  .advantages-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .works-flow {
    grid-template-columns: 1fr;
  }
  
  .case-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .section-title h2 {
    font-size: 2rem;
  }
  
  .title-cta {
    flex-direction: column;
  }
  
  .advantages-grid {
    grid-template-columns: 1fr;
  }
  
  .capabilities-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .how-it-works {
    padding: 30px 20px;
  }
  
  .case-example {
    padding: 25px 20px;
  }
  
  .review-cta {
    padding: 40px 20px;
  }
}
</style>

